import { useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { step8Schema, type Step8FormValues } from "@/schemas/onboarding-schema";
import { authClient } from "@/lib/auth-client";
import { api } from "@/lib/api";
import { OrgSelection } from "@/components/org-selection";
import FormShell from "@/components/form-shell";

export function OnboardingStep8() {
  const {
    organizations,
    showOrgSelection,
    selectedOrgId,
    setSelectedOrgId,
    isSettingActive,
    handleSetActiveOrganization,
    onCloseOrgSelection,
    updateOnboardingStep,
  } = useOnboarding("step8");

  const navigate = useNavigate();
  const location = useLocation();
  const paymentCancelled = useMemo(() => new URLSearchParams(location.search).get("error") === "cancelled", [location.search]);
  const { data: activeOrg } = authClient.useActiveOrganization();

  // Utiliser le hook dédié pour le statut des abonnements
  const { hasValidSubscription, subscription } = useSubscriptionStatus();

  // Mutation pour créer un essai gratuit
  const createFreeTrialMutation = api.useMutation("post", "/subscriptions/create-free-trial", {
    onSuccess: async () => {
      toast.success("Essai gratuit activé avec succès !");
      navigate("/subscriptions/confirm", { replace: true });
    },
    onError: (error) => {
      console.error("Erreur lors de la création de l'essai gratuit:", error);
      toast.error("Erreur lors de l'activation de l'essai gratuit", {
        description: error.message || "Une erreur inconnue s'est produite",
      });
    },
  });

  // Gérer le succès de paiement par lecture des query params et de l'état d'abonnement
  useEffect(() => {
    const success = new URLSearchParams(location.search).get("success") === "true";
    if (success && subscription && (subscription.status === "active" || subscription.status === "trialing")) {
      (async () => {
        await authClient.updateUser({ onboarding: "done" });
        toast.success("Abonnement annuel activé avec succès !");
        navigate("/subscriptions/confirm", { replace: true });
      })();
    }
  }, [location.search, subscription, navigate]);

  // Vérifier s'il y a un abonnement actif existant
  useEffect(() => {
    if (hasValidSubscription && subscription) {
      updateOnboardingStep("done");
      toast.success("Abonnement déjà actif !", {
        id: "subscription-already-active",
      });
    }
  }, [hasValidSubscription, subscription, updateOnboardingStep]);
  const trialEndDate = addDays(new Date(), 7);
  const formattedEndDate = format(trialEndDate, "dd/MM/yyyy", { locale: fr });

  const form = useForm<Step8FormValues>({
    resolver: zodResolver(step8Schema),
    defaultValues: {
      subscriptionType: "trial", // La période d'essai est sélectionnée par défaut
    },
  });

  const handleFreeTrial = async () => {
    createFreeTrialMutation.mutate({
      body: {},
    });
  };

  const handleAnnualSubscription = async () => {
    try {
      console.log("Attempting subscription upgrade with:", {
        plan: "standard",
        referenceId: activeOrg?.id,
        seats: 1,
      });

      await authClient.subscription.upgrade(
        {
          plan: "standard",
          referenceId: activeOrg!.id,
          successUrl: window.location.origin + "/subscriptions/confirm",
          cancelUrl: window.location.origin + "/onboarding/step8?error=cancelled",
        },
        {
          onError: (error) => {
            console.error("Detailed error:", error);
            console.error("Error activating subscription:", error.error?.message);
            toast.error("Erreur lors de l'activation de l'abonnement", {
              description: error.error?.message || "Une erreur inconnue s'est produite",
            });
          },
        }
      );
    } catch (error) {
      console.error("Catch block error:", error);
      toast.error("Erreur inattendue", {
        description: "Une erreur s'est produite lors de la création du paiement",
      });
    }
  };

  const onSubmit = async (data: Step8FormValues) => {
    if (data.subscriptionType === "trial") {
      await handleFreeTrial();
    } else {
      await handleAnnualSubscription();
    }
  };

  return (
    <>
      <FormShell title="Abonnement" subtitle="Commencez gratuitement ou profitez de l'offre annuelle" onSubmit={form.handleSubmit(onSubmit)}>
        {paymentCancelled && (
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>Paiement annulé</AlertTitle>
            <AlertDescription>Le processus de paiement a échoué. Vous pouvez sélectionner une option ci-dessous pour continuer.</AlertDescription>
          </Alert>
        )}

        <div className="mb-12 space-y-6">
          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-green-600">✓</div>
            <div>
              <h3 className="font-medium">Espace de réservation pour les familles relié à votre application de gestion</h3>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-green-600">✓</div>
            <div>
              <h3 className="font-medium">Organisez et gérez vos cours et activités en un clin d'œil</h3>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-green-600">✓</div>
            <div>
              <h3 className="font-medium">Accédez aux registres d'appels, fiches de fréquentation FNCP et à tous vos clients</h3>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-green-600">✓</div>
            <div>
              <h3 className="font-medium">Personnalisez vos prix selon vos besoins et vos offres</h3>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-green-600">✓</div>
            <div>
              <h3 className="font-medium">Attribuez des rôles, gérez les plannings et suivez vos animateurs</h3>
            </div>
          </div>
        </div>

        <div className="border-t pt-8">
          <Form {...form}>
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="subscriptionType"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex flex-col gap-4">
                        <label htmlFor="trial" className="cursor-pointer">
                          <Card className={`transition-colors hover:border-primary ${field.value === "trial" ? "border-primary" : "border-border"}`}>
                            <CardContent className="flex items-center p-4">
                              <RadioGroupItem value="trial" id="trial" className="mr-4" />
                              <div className="flex-1">
                                <div className="font-medium">Période d'essai</div>
                                <div className="text-sm text-muted-foreground">Se terminera le {formattedEndDate}</div>
                              </div>
                            </CardContent>
                          </Card>
                        </label>

                        <label htmlFor="annual" className="cursor-pointer">
                          <Card className={`transition-colors hover:border-primary ${field.value === "annual" ? "border-primary" : "border-border"}`}>
                            <CardContent className="flex items-center p-4">
                              <RadioGroupItem value="annual" id="annual" className="mr-4" />
                              <div className="flex-1">
                                <div className="font-medium">Abonnement annuel</div>
                                <div className="text-sm text-muted-foreground">480€/an, soit 40€/mois</div>
                              </div>
                            </CardContent>
                          </Card>
                        </label>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={createFreeTrialMutation.isPending}>
                {createFreeTrialMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  "Continuer"
                )}
              </Button>
            </div>
          </Form>
        </div>
      </FormShell>

      {/* Organisation selection dialog */}
      <OrgSelection
        open={showOrgSelection}
        organizations={organizations || []}
        selectedOrgId={selectedOrgId}
        onSelectedOrgChange={setSelectedOrgId}
        onConfirm={() => handleSetActiveOrganization(selectedOrgId)}
        onClose={onCloseOrgSelection}
        isLoading={isSettingActive}
      />
    </>
  );
}
